/**
 * useTaskLayerDrag Hook
 *
 * Implements drag-and-drop for task markers rendered as MapLibre Symbol Layer features.
 * When a user clicks and drags a marker in geometry editing mode, the marker position
 * updates in real-time via direct GeoJSON source manipulation (60fps, no React re-renders).
 *
 * Guard conditions for drag activation (ALL must be true):
 * 1. Geometry editing is active (task form open)
 * 2. Active tool is NOT Selection mode
 * 3. The clicked feature belongs to the currently selected task
 *
 * Supports both mouse and touch input for mobile compatibility.
 *
 * @module components/map/UnifiedAnchorSymbolLayer/hooks/useTaskLayerDrag
 */

import { useCallback, useEffect, useRef, type RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import maplibregl from 'maplibre-gl';

import type { FeatureCollection, Point } from 'geojson';

import { useGeoMarkupStore } from '@/store';
import { selectIsGeometryEditingActive } from '@/store/selectors';
import { GeometryType } from '@/types/geometry';
import type { Task } from '@/types';
import {
  CURSOR_GRAB,
  CURSOR_GRABBING,
  CURSOR_DEFAULT,
  MOUSE_BUTTON_RIGHT,
} from '@/constants/geometry';
import { LAYER_IDS } from '../styles/symbolLayerStyles';
import { MAP_CLUSTER_SOURCE_ID } from '../../MapClusterLayer/featureKind';

// ===== Types =====

interface UseTaskLayerDragProps {
  /** React ref to the MapLibre map component */
  mapRef: RefObject<MapRef | null>;
  /** Whether the map has finished initial loading */
  isMapReady: boolean;
  /** Current GeoJSON FeatureCollection from useTaskGeoJson */
  geojson: FeatureCollection<Point>;
  /** Array of tasks (needed to find geometry ID for dragged marker) */
  tasks: Task[];
}

interface DragState {
  /** Whether a drag operation is in progress */
  isDragging: boolean;
  /** Task ID of the feature being dragged */
  taskId: string;
  /** Cached index of the dragged feature in the snapshot for O(1) mousemove updates */
  featureIndex: number;
}

// ===== Hook =====

/**
 * Provides drag-and-drop functionality for Symbol Layer task markers.
 *
 * During drag:
 * - Map panning is disabled to prevent interference
 * - GeoJSON source is updated directly for 60fps visual feedback
 * - Cursor changes to 'grabbing'
 *
 * On drag end:
 * - Anchor point is updated in the Zustand store
 * - Marker geometry's calculatedCenter is updated
 * - Map panning is re-enabled
 *
 * @param props - Map ref, readiness flag, GeoJSON data, and tasks array
 */
export function useTaskLayerDrag({
  mapRef,
  isMapReady,
  geojson,
  tasks,
}: UseTaskLayerDragProps): void {
  // Guard condition selectors (atomic)
  const isGeometryEditingActive = useGeoMarkupStore(
    selectIsGeometryEditingActive
  );
  const activeTool = useGeoMarkupStore((state) => state.activeTool);

  // Actions (stable references - don't cause re-renders)
  const setAnchorPoint = useGeoMarkupStore(
    (state) => state.tasks.setFormAnchorPoint
  );
  const updateGeometry = useGeoMarkupStore((state) => state.updateGeometry);

  // Refs to hold latest prop values without causing useEffect re-registration
  const geojsonRef = useRef(geojson);
  geojsonRef.current = geojson;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  // Drag state ref (not React state - avoids re-renders during drag)
  const dragStateRef = useRef<DragState | null>(null);

  // Snapshot of GeoJSON at drag start (avoids recalculation on every mousemove)
  const geoJsonSnapshotRef = useRef<FeatureCollection<Point> | null>(null);

  /**
   * Checks whether drag is currently allowed based on guard conditions.
   * Uses getState() to read fresh values without stale closures.
   */
  const isDragEnabled = useCallback((): boolean => {
    const state = useGeoMarkupStore.getState();
    const editing = selectIsGeometryEditingActive(state);
    const tool = state.activeTool;

    return editing && tool !== GeometryType.Selection;
  }, []);

  /**
   * Checks whether a clicked feature belongs to the currently selected task.
   * Uses getState() for fresh selectedId value.
   */
  const isFeatureDraggable = useCallback((featureTaskId: string): boolean => {
    const state = useGeoMarkupStore.getState();
    return state.tasks.selectedId === featureTaskId;
  }, []);

  /**
   * Updates the GeoJSON source with modified coordinates for the dragged feature.
   * Uses the cached feature index for O(1) lookup instead of iterating all features.
   */
  const updateSourceDuringDrag = useCallback(
    (
      map: maplibregl.Map,
      featureIndex: number,
      lng: number,
      lat: number
    ): void => {
      const snapshot = geoJsonSnapshotRef.current;
      if (!snapshot) return;

      const originalFeature = snapshot.features[featureIndex];
      if (!originalFeature) return;

      const updatedFeature = {
        ...originalFeature,
        geometry: {
          ...originalFeature.geometry,
          coordinates: [lng, lat],
        },
      };

      // Pre-split array: left + updated + right (avoids full .map() iteration)
      const updatedFeatures = [
        ...snapshot.features.slice(0, featureIndex),
        updatedFeature,
        ...snapshot.features.slice(featureIndex + 1),
      ];

      const updatedGeoJson: FeatureCollection<Point> = {
        ...snapshot,
        features: updatedFeatures,
      };

      const source = map.getSource(
        MAP_CLUSTER_SOURCE_ID
      ) as maplibregl.GeoJSONSource;
      if (source) {
        source.setData(updatedGeoJson);
      }
    },
    []
  );

  /**
   * Finds the marker geometry ID for a task and dispatches the final position
   * update to the Zustand store. Reads from tasksRef for latest data.
   */
  const commitDragPosition = useCallback(
    (taskId: string, lng: number, lat: number): void => {
      const task = tasksRef.current.find(
        (searchTask) => searchTask.id === taskId
      );
      if (!task) return;

      const markerGeometry = task.geometries?.find(
        (geometry) => geometry.type === GeometryType.Marker
      );

      // Update anchor point (task-level position)
      setAnchorPoint({ lat, lng });

      // Update marker geometry's calculated center if it exists
      if (markerGeometry) {
        updateGeometry(markerGeometry.id, {
          calculatedCenter: { lat, lng },
        });
      }
    },
    [setAnchorPoint, updateGeometry]
  );

  // =========================================================================
  // EVENT REGISTRATION
  // =========================================================================

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !isMapReady) return;

    // Only register events when guard conditions could be met
    if (!isGeometryEditingActive || activeTool === GeometryType.Selection)
      return;

    // ===== MOUSE HANDLERS =====

    const handleMouseDown = (event: maplibregl.MapMouseEvent): void => {
      if (event.originalEvent.button === MOUSE_BUTTON_RIGHT) return;
      if (!isDragEnabled()) return;

      // Query for marker shapes at the click point
      const features = map.queryRenderedFeatures(event.point, {
        layers: [LAYER_IDS.MARKER_COMPOSITE],
      });

      if (features.length === 0) return;

      const clickedFeature = features[0];
      const featureTaskId = clickedFeature?.properties?.id as
        | string
        | undefined;
      if (!featureTaskId || !isFeatureDraggable(featureTaskId)) return;

      // Snapshot current GeoJSON and find the dragged feature index
      const currentGeojson = geojsonRef.current;
      const featureIndex = currentGeojson.features.findIndex(
        (feature) => feature.properties?.id === featureTaskId
      );
      if (featureIndex === -1) return;

      // Start drag
      event.preventDefault();
      map.dragPan.disable();
      map.getCanvas().style.cursor = CURSOR_GRABBING;

      geoJsonSnapshotRef.current = currentGeojson;
      dragStateRef.current = {
        isDragging: true,
        taskId: featureTaskId,
        featureIndex,
      };
    };

    const handleMouseMove = (event: maplibregl.MapMouseEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState?.isDragging) return;

      updateSourceDuringDrag(
        map,
        dragState.featureIndex,
        event.lngLat.lng,
        event.lngLat.lat
      );
    };

    const handleMouseUp = (event: maplibregl.MapMouseEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState?.isDragging) return;

      // Re-enable map panning
      map.dragPan.enable();
      map.getCanvas().style.cursor = CURSOR_DEFAULT;

      // Commit final position to store
      commitDragPosition(dragState.taskId, event.lngLat.lng, event.lngLat.lat);

      // Clear drag state
      dragStateRef.current = null;
      geoJsonSnapshotRef.current = null;
    };

    // ===== TOUCH HANDLERS =====

    const handleTouchStart = (event: maplibregl.MapTouchEvent): void => {
      // Only handle single touch (ignore pinch-zoom)
      if (event.originalEvent.touches.length !== 1) return;
      if (!isDragEnabled()) return;

      const features = map.queryRenderedFeatures(event.point, {
        layers: [LAYER_IDS.MARKER_COMPOSITE],
      });

      if (features.length === 0) return;

      const clickedFeature = features[0];
      const featureTaskId = clickedFeature?.properties?.id as
        | string
        | undefined;
      if (!featureTaskId || !isFeatureDraggable(featureTaskId)) return;

      // Snapshot current GeoJSON and find the dragged feature index
      const currentGeojson = geojsonRef.current;
      const featureIndex = currentGeojson.features.findIndex(
        (feature) => feature.properties?.id === featureTaskId
      );
      if (featureIndex === -1) return;

      // Start drag
      event.preventDefault();
      map.dragPan.disable();
      map.getCanvas().style.cursor = CURSOR_GRABBING;

      geoJsonSnapshotRef.current = currentGeojson;
      dragStateRef.current = {
        isDragging: true,
        taskId: featureTaskId,
        featureIndex,
      };
    };

    const handleTouchMove = (event: maplibregl.MapTouchEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState?.isDragging) return;
      if (event.originalEvent.touches.length !== 1) return;

      event.preventDefault();

      updateSourceDuringDrag(
        map,
        dragState.featureIndex,
        event.lngLat.lng,
        event.lngLat.lat
      );
    };

    const handleTouchEnd = (event: maplibregl.MapTouchEvent): void => {
      const dragState = dragStateRef.current;
      if (!dragState?.isDragging) return;
      if (event.originalEvent.changedTouches.length === 0) return;

      // Re-enable map panning
      map.dragPan.enable();
      map.getCanvas().style.cursor = CURSOR_DEFAULT;

      // Commit final position to store
      commitDragPosition(dragState.taskId, event.lngLat.lng, event.lngLat.lat);

      // Clear drag state
      dragStateRef.current = null;
      geoJsonSnapshotRef.current = null;
    };

    // ===== HOVER CURSOR (grab cursor for draggable markers) =====

    const handleMouseEnterMarker = (): void => {
      if (!dragStateRef.current?.isDragging && isDragEnabled()) {
        map.getCanvas().style.cursor = CURSOR_GRAB;
      }
    };

    const handleMouseLeaveMarker = (): void => {
      if (!dragStateRef.current?.isDragging) {
        map.getCanvas().style.cursor = CURSOR_DEFAULT;
      }
    };

    // ===== REGISTER EVENTS =====

    // Layer-specific for mousedown (only triggers on marker shapes)
    map.on('mousedown', LAYER_IDS.MARKER_COMPOSITE, handleMouseDown);

    // Layer-specific hover cursor events
    map.on('mouseenter', LAYER_IDS.MARKER_COMPOSITE, handleMouseEnterMarker);
    map.on('mouseleave', LAYER_IDS.MARKER_COMPOSITE, handleMouseLeaveMarker);

    // Map-level for mousemove and mouseup (user may move cursor off marker during drag)
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);

    // Touch events (map-level for start to support layer query, map-level for move/end)
    map.on('touchstart', LAYER_IDS.MARKER_COMPOSITE, handleTouchStart);
    map.on('touchmove', handleTouchMove);
    map.on('touchend', handleTouchEnd);

    // ===== CLEANUP =====

    return () => {
      map.off('mousedown', LAYER_IDS.MARKER_COMPOSITE, handleMouseDown);
      map.off('mouseenter', LAYER_IDS.MARKER_COMPOSITE, handleMouseEnterMarker);
      map.off('mouseleave', LAYER_IDS.MARKER_COMPOSITE, handleMouseLeaveMarker);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      map.off('touchstart', LAYER_IDS.MARKER_COMPOSITE, handleTouchStart);
      map.off('touchmove', handleTouchMove);
      map.off('touchend', handleTouchEnd);

      // Ensure drag state is cleaned up on unmount
      if (dragStateRef.current?.isDragging) {
        map.dragPan.enable();
        map.getCanvas().style.cursor = CURSOR_DEFAULT;
        dragStateRef.current = null;
        geoJsonSnapshotRef.current = null;
      }
    };
  }, [
    mapRef,
    isMapReady,
    isGeometryEditingActive,
    activeTool,
    isDragEnabled,
    isFeatureDraggable,
    updateSourceDuringDrag,
    commitDragPosition,
  ]);
}
